import { useCallback, useEffect, useRef, useState } from 'react';
import { logoutUser } from '../firebase';

// HIPAA "automatic logoff" (§164.312(a)(2)(iii)) — sign the user out after a
// stretch of no interaction so an unattended session on a shared device can't
// expose PHI.
//
// The timeout is UNCONDITIONAL: it is a security control, not a preference, so
// the login page's "Keep me signed in" (which selects Firebase local vs session
// persistence — i.e. whether the session survives closing the browser) does NOT
// extend it. See src/pages/Login.tsx.
//
// To avoid silently dumping someone mid-read, the last `warnMs` of the window
// is a COUNTDOWN: the hook flips `warning` true and the app shows a dialog with
// a "Stay signed in" button. During the countdown ordinary activity (mouse
// movement etc.) deliberately does NOT dismiss the warning — an explicit click
// is required, so a jostled mouse or a resting hand can't hold a PHI session
// open indefinitely.
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_WARN_MS = 60 * 1000;

// Re-arming on every mousemove would thrash timers; once a second is plenty.
const REARM_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
];

export interface IdleLogoutState {
  /** True during the final countdown — render the warning dialog. */
  warning: boolean;
  /** Whole seconds remaining before automatic sign-out. */
  secondsLeft: number;
  /** Dismiss the warning and restart the full idle window. */
  staySignedIn: () => void;
  /** Sign out immediately. */
  signOutNow: () => void;
}

export function useIdleLogout(
  enabled: boolean,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  warnMs: number = DEFAULT_WARN_MS,
): IdleLogoutState {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warnMs / 1000));

  const warnTimer = useRef<number | null>(null);
  const logoutTimer = useRef<number | null>(null);
  const tick = useRef<number | null>(null);
  // Mirrors `warning` so event listeners can read it without being re-bound.
  const warningRef = useRef(false);
  const lastArm = useRef(0);

  const clearAll = useCallback(() => {
    if (warnTimer.current !== null) { window.clearTimeout(warnTimer.current); warnTimer.current = null; }
    if (logoutTimer.current !== null) { window.clearTimeout(logoutTimer.current); logoutTimer.current = null; }
    if (tick.current !== null) { window.clearInterval(tick.current); tick.current = null; }
  }, []);

  // Enter the final countdown.
  const startCountdown = useCallback(() => {
    warningRef.current = true;
    setWarning(true);
    setSecondsLeft(Math.ceil(warnMs / 1000));
    tick.current = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    // logoutUser() navigates to /Login and reloads, clearing in-memory state.
    logoutTimer.current = window.setTimeout(() => { logoutUser(); }, warnMs);
  }, [warnMs]);

  // (Re)start the full idle window.
  const arm = useCallback(() => {
    clearAll();
    if (warningRef.current) {
      warningRef.current = false;
      setWarning(false);
    }
    warnTimer.current = window.setTimeout(startCountdown, Math.max(0, timeoutMs - warnMs));
  }, [clearAll, startCountdown, timeoutMs, warnMs]);

  const staySignedIn = useCallback(() => {
    lastArm.current = Date.now();
    arm();
  }, [arm]);

  const signOutNow = useCallback(() => {
    clearAll();
    logoutUser();
  }, [clearAll]);

  useEffect(() => {
    if (!enabled) {
      clearAll();
      warningRef.current = false;
      setWarning(false);
      return undefined;
    }

    arm();
    lastArm.current = Date.now();

    const onActivity = () => {
      // During the countdown only an explicit click counts (see header note).
      if (warningRef.current) return;
      const now = Date.now();
      if (now - lastArm.current < REARM_THROTTLE_MS) return;
      lastArm.current = now;
      arm();
    };
    // Returning to the tab counts as activity; LEAVING it does not (the clock
    // keeps running on a backgrounded tab, which is the point of the control).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onActivity();
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    for (const ev of ACTIVITY_EVENTS) document.addEventListener(ev, onActivity, opts);
    document.addEventListener('visibilitychange', onVisibility, opts);

    return () => {
      clearAll();
      for (const ev of ACTIVITY_EVENTS) document.removeEventListener(ev, onActivity, opts);
      document.removeEventListener('visibilitychange', onVisibility, opts);
    };
  }, [enabled, arm, clearAll]);

  return { warning, secondsLeft, staySignedIn, signOutNow };
}
